import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FilePlus2, PlayCircle } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { BILLING_ITEM_STATUS, CLIENT_INV_STATUS, fmtDate, fmtDateTime, fmtMoney, MILESTONE_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ToneChip } from "../../components/common";
import { DemoBanner, Money } from "../../components/finance";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const CI_ACTIONS = {
  borrador: ["solicitar_aprobacion"],
  aprobada_emision: ["enviar_erp"],
  enviada_erp: ["registrar_emision"],
  emitida: ["enviar_cliente"],
  enviada_cliente: ["registrar_cobro", "generar_recordatorio"],
  vencida: ["registrar_cobro", "generar_recordatorio"],
  parcialmente_cobrada: ["registrar_cobro", "generar_recordatorio"],
};
const CI_LABELS = {
  solicitar_aprobacion: "Solicitar aprobación", enviar_erp: "Enviar a ERP (mock)", registrar_emision: "Registrar emisión",
  enviar_cliente: "Enviar al cliente", registrar_cobro: "Registrar cobro", generar_recordatorio: "Generar recordatorio",
};

function ClientInvoiceDialog({ iid, open, onClose, onDone }) {
  const { map } = useUsers();
  const [amount, setAmount] = useState("");
  const { data, refetch } = useQuery({
    queryKey: ["fin-cinv", iid],
    queryFn: async () => (await api.get(`/finanzas/billing/invoices/${iid}`)).data,
    enabled: open,
  });
  if (!data) return null;
  const { invoice: inv, receipts, approvals } = data;

  const act = async (action) => {
    try {
      await api.post(`/finanzas/billing/invoices/${inv.id}/action`, {
        action, confirm: action === "enviar_cliente" ? true : undefined,
        amount: action === "registrar_cobro" && amount ? Number(amount) : undefined,
      });
      toast.success("Acción registrada");
      setAmount("");
      refetch();
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="client-invoice-dialog">
        <DialogHeader><DialogTitle>Factura a cliente — {inv.client}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Chip map={CLIENT_INV_STATUS} value={inv.status} testid="cinv-status" />
            <span className="font-mono text-xs text-slate-500">{inv.definitive_number || "sin número definitivo"}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm">
            <div><p className="ts-caption">Concepto</p><p className="text-xs">{inv.concept}</p></div>
            <div><p className="ts-caption">Total (IVA incl.)</p><Money value={inv.total} className="font-bold" /></div>
            <div><p className="ts-caption">Cobrado</p><Money value={inv.collected} className="font-bold text-emerald-700" /></div>
            <div><p className="ts-caption">Vencimiento</p><p className="text-xs">{fmtDate(inv.due_date)}</p></div>
            <div><p className="ts-caption">Evidencia</p><p className="text-xs">{inv.evidence_ref || "no requerida"}</p></div>
            <div><p className="ts-caption">Responsable cobro</p><p className="text-xs">{userName(map, inv.ar_owner_id)}</p></div>
          </div>
          {(CI_ACTIONS[inv.status] || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="cinv-actions">
              {(CI_ACTIONS[inv.status] || []).map((a) => (
                <Button key={a} size="sm" variant={a === "generar_recordatorio" ? "outline" : "default"} onClick={() => act(a)} data-testid={`cinv-${a}`}>
                  <PlayCircle className="mr-1 h-3.5 w-3.5" /> {CI_LABELS[a]}
                </Button>
              ))}
              {(CI_ACTIONS[inv.status] || []).includes("registrar_cobro") && (
                <Input type="number" placeholder={`Importe (máx. ${(inv.total - inv.collected).toFixed(2)} €)`} value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 w-44 text-xs" data-testid="cinv-collect-amount" />
              )}
            </div>
          )}
          {receipts.length > 0 && (
            <div data-testid="cinv-receipts">
              <p className="mb-1 text-xs font-semibold text-slate-500">Cobros registrados</p>
              {receipts.map((r) => <p key={r.id} className="text-xs text-slate-600">{r.reference} — {fmtMoney(r.amount)} · {fmtDateTime(r.date)}</p>)}
            </div>
          )}
          {approvals.length > 0 && (
            <p className="text-xs text-slate-400">Aprobación de emisión: {approvals[0].status}{approvals[0].decision_comment ? ` — ${approvals[0].decision_comment}` : ""}</p>
          )}
          <p className="rounded-sm border border-sky-200 bg-sky-50 p-2 text-xs text-sky-800">DOCUMENTO DEMO — NO ES FACTURA FISCAL REAL. El ERP y el envío al cliente son simulados.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Facturacion() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const { data: billing, refetch } = useQuery({
    queryKey: ["fin-billing"],
    queryFn: async () => (await api.get("/finanzas/billing/items")).data,
  });
  const { data: invoices } = useQuery({
    queryKey: ["fin-cinvoices", status],
    queryFn: async () => (await api.get("/finanzas/billing/invoices", { params: { status: status || undefined } })).data,
  });
  const openInv = params.get("inv");

  const fromMilestone = async (ms) => {
    try {
      await api.post("/finanzas/billing/items/from-milestone", { milestone_id: ms.id });
      toast.success("Hito convertido en elemento facturable");
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };

  const createInvoice = async (item) => {
    try {
      const r = await api.post("/finanzas/billing/invoices", { billing_item_id: item.id });
      toast.success("Borrador de factura creado — solicita aprobación para emitir");
      refetch();
      qc.invalidateQueries({ queryKey: ["fin-cinvoices"] });
      setParams({ inv: r.data.id });
    } catch (e) { toast.error(apiError(e)); }
  };

  const readyItems = (billing?.items || []).filter((i) => i.status === "preparado_facturar");
  const readyMilestones = (billing?.milestones || []).filter((m) => m.status !== "bloqueado" && !(billing?.items || []).some((i) => i.milestone_id === m.id));

  return (
    <div data-testid="facturacion-page">
      <PageHeader title="Facturación a clientes" subtitle="Del hito contractual al cobro: evidencia, aprobación de emisión, ERP mock, envío confirmado y seguimiento de cobros." />
      <DemoBanner />

      <h2 className="ts-section-title mb-3 mt-6">Hitos facturables</h2>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="ts-card overflow-hidden" data-testid="billing-items">
          <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Preparados para facturar ({readyItems.length})</p>
          <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
            {readyItems.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 px-5 py-2.5" data-testid={`billing-item-${i.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{i.milestone}</p>
                  <p className="text-xs text-slate-500">{i.client} · {fmtDate(i.planned_date)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Money value={i.amount} className="text-xs" />
                  <Button size="sm" variant="outline" onClick={() => createInvoice(i)} data-testid={`create-cinv-${i.id}`}><FilePlus2 className="mr-1 h-3.5 w-3.5" /> Crear factura</Button>
                </div>
              </div>
            ))}
            {!readyItems.length && <p className="px-5 py-6 text-center text-sm text-slate-500">Sin elementos preparados.</p>}
          </div>
        </div>
        <div className="ts-card overflow-hidden" data-testid="billing-milestones">
          <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Hitos contractuales próximos ({readyMilestones.length})</p>
          <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
            {readyMilestones.slice(0, 20).map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 px-5 py-2.5" data-testid={`milestone-${m.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm">{m.milestone}</p>
                  <p className="text-xs text-slate-500">{fmtDate(m.planned_date)} {m.required_evidence ? (m.evidence_ref ? "· evidencia aceptada" : "· SIN EVIDENCIA") : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Chip map={MILESTONE_STATUS} value={m.status} />
                  <Money value={m.amount} className="text-xs" />
                  <Button size="sm" variant="ghost" onClick={() => fromMilestone(m)} data-testid={`to-billing-${m.id}`}>Facturable →</Button>
                </div>
              </div>
            ))}
            {!readyMilestones.length && <p className="px-5 py-6 text-center text-sm text-slate-500">Sin hitos pendientes de convertir.</p>}
          </div>
        </div>
      </div>

      <div className="mb-4 mt-8 flex items-center justify-between">
        <h2 className="ts-section-title">Facturas emitidas a clientes</h2>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-60" data-testid="cinv-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(CLIENT_INV_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {invoices && invoices.length === 0 && <EmptyState title="Sin facturas de cliente" description="Crea la primera desde un hito facturable." />}
      {invoices?.length > 0 && (
        <div className="ts-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Número</TableHead><TableHead>Cliente</TableHead><TableHead>Concepto</TableHead><TableHead>Vencimiento</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Cobrado</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} data-testid={`cinv-row-${inv.id}`} className={`cursor-pointer ${openInv === inv.id ? "bg-blue-50/60" : ""} ${inv.status === "vencida" ? "bg-red-50/40" : ""}`} onClick={() => setParams({ inv: inv.id })}>
                  <TableCell className="font-mono text-xs">{inv.definitive_number || "borrador"}</TableCell>
                  <TableCell className="text-sm">{inv.client}</TableCell>
                  <TableCell className="max-w-56 truncate text-xs">{inv.concept}</TableCell>
                  <TableCell className="text-xs">{fmtDate(inv.due_date)}{" "}{inv.status === "vencida" && <span className="ml-1.5 inline-block"><ToneChip label={`${inv.days_overdue ?? ""} días`} tone="danger" /></span>}</TableCell>
                  <TableCell><Chip map={CLIENT_INV_STATUS} value={inv.status} testid={`cinv-status-${inv.id}`} /></TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(inv.total)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-emerald-700">{fmtMoney(inv.collected)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {openInv && <ClientInvoiceDialog iid={openInv} open onClose={() => setParams({})} onDone={() => qc.invalidateQueries({ queryKey: ["fin-cinvoices"] })} />}
    </div>
  );
}
